import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Activity, CalendarRange, LayoutDashboard, RefreshCw, SlidersHorizontal, Store } from "lucide-react";
import CommonDatePicker from "../../Common/CommonDatePicker";
import { D2C_OPS_SUMMARY_API_URL, D2C_OPS_SWITCHES_API_URL } from "../../Shared/constant";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input, NativeSelect } from "./ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { TooltipProvider } from "./ui/tooltip";
import ActivityList from "./ActivityList";
import BarList from "./BarList";
import D2CStores from "./D2CStores";
import DealerTable from "./DealerTable";
import Funnel from "./Funnel";
import HeadlineTiles from "./HeadlineTiles";
import { ErpPanel, SystemPanel } from "./HealthPanels";
import OrderDetailDrawer from "./OrderDetailDrawer";
import OrdersList from "./OrdersList";
import RegionTable from "./RegionTable";
import RoutingSwitches from "./RoutingSwitches";
import StateBlock, { Busy, EmptyState, NoAccess } from "./StateBlock";
import useD2cGet from "./useD2cGet";
import { hasD2cOpsAccess, hasD2cSwitchesAccess } from "./d2cAccess";
import { SUMMARY_MAX_DAYS, buildQuery, istDaysAgo, rangeDays, toIstDate } from "./d2cFormat";
import "./ui/tailwind.css";

const CITY_DEBOUNCE_MS = 500;

const VIEWS = [
  { key: "activity", label: "Activity", icon: Activity },
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "switches", label: "Routing switches", icon: SlidersHorizontal },
  { key: "stores", label: "Stores", icon: Store },
];

// The staging build's API host carries a staging-style word; the live ones do not.
const BASE_URL = process.env.REACT_APP_BASE_URL || "";
export const ENV_LABEL = !BASE_URL ? null : /(staging|test|dev|uat)/i.test(BASE_URL) ? "Staging" : "Live";

const timeOfDay = (date) =>
  date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });

// The page root: every D2C component renders inside this wrapper, which scopes
// the Tailwind tokens (d2c-ui) and provides tooltips to the whole page,
// including sheets and dialogs rendered in portals.
const Shell = ({ children }) => (
  <TooltipProvider delayDuration={200}>
    <section
      data-d2c-root=""
      className="Main_section d2c-ui tw-float-left tw-h-full tw-w-full tw-overflow-y-auto tw-overflow-x-hidden tw-rounded-lg tw-bg-page tw-p-4 tw-font-sans tw-text-sm tw-leading-5 tw-text-foreground tw-antialiased"
    >
      {children}
    </section>
  </TooltipProvider>
);

// The D2C dealer-fulfilment flow over the OMS ops API: live activity, a
// read-only dashboard, and the routing and store switches (changeable with
// d2c-switches). No customer name, phone or address is requested or shown here.
const D2CDashboard = () => {
  const { selfInfo } = useSelector((state) => state.adminReducers);
  const allowed = hasD2cOpsAccess(selfInfo);
  const canSwitch = hasD2cSwitchesAccess(selfInfo);
  const [view, setView] = useState("activity");
  // A tab mounts on its first visit and then stays mounted, so its filters
  // and pages survive switching back and forth.
  const [visited, setVisited] = useState({ activity: true });

  const [dateRange, setDateRange] = useState(() => [istDaysAgo(6), istDaysAgo(0)]);
  const [storeId, setStoreId] = useState("");
  const [regionId, setRegionId] = useState("");
  const [cityInput, setCityInput] = useState("");
  const [city, setCity] = useState("");
  const [dealerOptions, setDealerOptions] = useState([]);
  const [openOrderId, setOpenOrderId] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [lastRefreshed, setLastRefreshed] = useState(() => new Date());

  useEffect(() => {
    const timer = setTimeout(() => setCity(cityInput.trim()), CITY_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [cityInput]);

  const from = toIstDate(dateRange[0]);
  const to = toIstDate(dateRange[1]);
  const rangeReady = Boolean(from && to);
  // The OMS refuses a longer summary with a 400; say why instead of asking.
  const rangeTooLong = rangeReady && rangeDays(from, to) > SUMMARY_MAX_DAYS;

  const summaryUrl =
    allowed && rangeReady && !rangeTooLong && visited.dashboard
      ? D2C_OPS_SUMMARY_API_URL + buildQuery({ from, to, store_id: storeId, city, region_id: regionId })
      : null;
  const summary = useD2cGet(summaryUrl);
  const data = summary.data;

  // One GET feeds both the region filter and the switches panel.
  const switches = useD2cGet(allowed && (visited.dashboard || visited.switches) ? D2C_OPS_SWITCHES_API_URL : null);
  const regionOptions = switches.data?.regions || [];

  const showView = (key) => {
    setView(key);
    setVisited((previous) => ({ ...previous, [key]: true }));
  };

  // The dealer filter lists every dealer seen so far, so picking one does not
  // shrink the list to that dealer alone.
  useEffect(() => {
    if (!data?.dealers?.length) return;
    setDealerOptions((previous) => {
      const byId = new Map(previous.map((dealer) => [dealer.store_id, dealer]));
      data.dealers.forEach((dealer) => {
        if (dealer.store_id) byId.set(dealer.store_id, dealer);
      });
      return [...byId.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    });
  }, [data]);

  // Reload whatever the visited tabs show: summary, switches, and (through
  // refreshTick) the activity, orders and stores lists.
  const refreshAll = () => {
    if (summaryUrl) summary.reload();
    if (visited.dashboard || visited.switches) switches.reload();
    setRefreshTick((tick) => tick + 1);
    setLastRefreshed(new Date());
  };

  if (!allowed) {
    return (
      <Shell>
        <NoAccess />
      </Shell>
    );
  }

  const busy = summary.loading || switches.loading;

  return (
    <Shell>
      <header className="tw-mb-4 tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
        <div className="tw-min-w-0">
          <div className="tw-flex tw-items-center tw-gap-2">
            <h1 className="tw-m-0 tw-font-sans tw-text-xl tw-font-semibold tw-leading-7 tw-tracking-tight tw-text-foreground">
              D2C fulfilment
            </h1>
            {ENV_LABEL && <Badge tone={ENV_LABEL === "Live" ? "success" : "warning"}>{ENV_LABEL}</Badge>}
          </div>
          <p className="tw-m-0 tw-mt-0.5 tw-text-sm tw-text-muted-foreground">
            Website bike orders fulfilled by dealers. Times are IST.
          </p>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-xs tw-text-muted-foreground">Updated {timeOfDay(lastRefreshed)}</span>
          <Button variant="outline" size="icon" onClick={refreshAll} aria-label="Refresh data" title="Refresh data">
            {busy ? <Busy label="Refreshing…" /> : <RefreshCw aria-hidden="true" />}
          </Button>
        </div>
      </header>

      <Tabs value={view} onValueChange={showView}>
        <TabsList aria-label="D2C sections">
          {VIEWS.map((entry) => {
            const Icon = entry.icon;
            return (
              <TabsTrigger key={entry.key} value={entry.key}>
                <Icon aria-hidden="true" />
                {entry.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="activity" forceMount hidden={view !== "activity"}>
          <ActivityList
            visible={view === "activity"}
            onOpen={setOpenOrderId}
            refreshTick={refreshTick}
            onAutoRefresh={() => setLastRefreshed(new Date())}
          />
        </TabsContent>

        <TabsContent value="dashboard" forceMount hidden={view !== "dashboard"}>
          {visited.dashboard && (
            <div className="tw-space-y-4">
              <Card>
                <CardHeader className="tw-items-center tw-gap-2 tw-py-3">
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <div className="common_datepicker [&_.datepickField_new]:tw-w-60">
                      <CommonDatePicker dateRange={dateRange} setDateRange={setDateRange} placeholderText="Select a date range" />
                    </div>
                    <NativeSelect
                      value={storeId}
                      onChange={(e) => setStoreId(e.target.value)}
                      aria-label="Filter by dealer"
                      className="tw-w-52"
                    >
                      <option value="">All dealers</option>
                      {dealerOptions.map((dealer) => (
                        <option key={dealer.store_id} value={dealer.store_id}>
                          {dealer.name || dealer.store_id}
                          {dealer.franchise_code ? ` (${dealer.franchise_code})` : ""}
                        </option>
                      ))}
                    </NativeSelect>
                    <NativeSelect
                      value={regionId}
                      onChange={(e) => setRegionId(e.target.value)}
                      aria-label="Filter by region"
                      className="tw-w-44"
                    >
                      <option value="">All regions</option>
                      {regionOptions.map((region) => (
                        <option key={region.region_id} value={region.region_id}>
                          {region.name || region.region_id}
                        </option>
                      ))}
                    </NativeSelect>
                    <Input
                      value={cityInput}
                      onChange={(e) => setCityInput(e.target.value)}
                      placeholder="City"
                      aria-label="Filter by city"
                      className="tw-w-40"
                    />
                  </div>
                  {data?.range && (
                    <span className="tw-text-xs tw-text-muted-foreground">
                      Showing {data.range.from} to {data.range.to}
                    </span>
                  )}
                </CardHeader>
              </Card>

              {!rangeReady ? (
                <Card>
                  <EmptyState icon={CalendarRange}>Pick a start and an end date to see the numbers.</EmptyState>
                </Card>
              ) : (
                <>
                  {rangeTooLong ? (
                    <Card>
                      <EmptyState icon={CalendarRange}>
                        Pick a range of {SUMMARY_MAX_DAYS} days or less to see the numbers.
                      </EmptyState>
                    </Card>
                  ) : (
                    <StateBlock loading={summary.loading} error={summary.error} onRetry={summary.reload} isEmpty={!data}>
                      {data && (
                        <div className="tw-space-y-4">
                          <HeadlineTiles headline={data.headline} />

                          <div className="tw-grid tw-gap-4 lg:tw-grid-cols-2">
                            <Funnel headline={data.headline} />
                            <BarList title="Outcomes" counts={data.outcomes} hint="Where each held order ended up." />
                          </div>

                          <div className="tw-grid tw-gap-4 md:tw-grid-cols-2 xl:tw-grid-cols-3">
                            <BarList title="Not held for dealers" counts={data.not_held} hint="Website bike orders that never entered the dealer flow." tone="grey" />
                            <BarList title="Not offered" counts={data.not_offered} hint="Held orders handed back without an offer." tone="amber" />
                            <BarList title="Drop-offs" counts={data.dropoffs} hint="Orders that left the dealer flow part-way." tone="red" />
                            <BarList title="Decline reasons" counts={data.decline_reasons} tone="amber" />
                            <BarList title="Can't fulfil reasons" counts={data.cant_fulfil_reasons} tone="red" />
                          </div>

                          <RegionTable regions={data.regions} />

                          <Card>
                            <CardHeader>
                              <div>
                                <CardTitle>Dealers ({data.dealers?.length || 0})</CardTitle>
                                <CardDescription>Click a heading to sort.</CardDescription>
                              </div>
                            </CardHeader>
                            {data.dealers?.length ? (
                              <DealerTable dealers={data.dealers} />
                            ) : (
                              <EmptyState icon={Store}>No dealer received an offer in this range.</EmptyState>
                            )}
                          </Card>

                          <div className="tw-grid tw-gap-4 lg:tw-grid-cols-2">
                            <ErpPanel erp={data.erp} />
                            <SystemPanel system={data.system} />
                          </div>
                        </div>
                      )}
                    </StateBlock>
                  )}

                  <OrdersList
                    from={from}
                    to={to}
                    storeId={storeId}
                    regionId={regionId}
                    city={city}
                    refreshTick={refreshTick}
                    onOpen={setOpenOrderId}
                  />
                </>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="switches" forceMount hidden={view !== "switches"}>
          {visited.switches && <RoutingSwitches switches={switches} canEdit={canSwitch} />}
        </TabsContent>

        <TabsContent value="stores" forceMount hidden={view !== "stores"}>
          {visited.stores && <D2CStores canEdit={canSwitch} refreshTick={refreshTick} />}
        </TabsContent>
      </Tabs>

      <OrderDetailDrawer orderId={openOrderId} onClose={() => setOpenOrderId(null)} />
    </Shell>
  );
};

export default D2CDashboard;
